// Competency analysis: item scores rolled up to Learning Competencies, computed on
// demand from results + answer key + the quiz's Table of Specifications (never
// stored, like itemAnalysis.ts).
//
// Answers two questions the item-level report can't:
//   1. Which learners need remediation on each learning competency?
//   2. What is each learner's mastered / least-mastered competency?
//
// Two independent classifications are reported per percentage, because they answer
// different questions:
//   * mastery level — the TESTIFY-aligned 4-band descriptor, same as MPS.
//   * instructional response — DepEd's SY 2026-2027 transition table: 75 is the
//     passing mark, 75-79 is passing-but-guided-practice, 65-74 targeted
//     remediation, 0-64 sustained intervention. So "below the cut-off" and "needs
//     remediation" are the same set, and 75-79 is surfaced separately.
//
// Scoring reuses scoring/score.ts, so blanks and double-marks are treated exactly
// as they are in the total score (zero, never guessed) and per-item points from a
// TESTIFY key are honoured.
import type { AnswerKeyRecord, CompetencyMapRecord, ResultRecord, StudentRecord } from '../db/schema.js';
import { scoreResponse, keyItems } from '../scoring/score.js';
import { masteryDescriptor } from './itemAnalysis.js';
import {
  MIN_ITEMS_FOR_LEARNER,
  MASTERY_CUTOFF,
  competencyLabel,
  interventionFor,
  type Intervention,
} from './competencyMap.js';

/** One learner's standing on one competency. */
export interface LearnerCompetencyScore {
  /** positional label ("C1") — the competency's identity is its description. */
  label: string;
  description: string;
  earned: number;
  possible: number;
  /** rounded whole percent; null when the key covers none of the mapped items. */
  pct: number | null;
  mastered: boolean;
  /** 4-band mastery descriptor (same bands as MPS). */
  band: string;
  /** DepEd instructional response for this percentage. */
  intervention: Intervention;
  /** false when the competency has too few items for a per-learner claim. */
  reliable: boolean;
}

/** A learner and their competency profile (report #2). */
export interface LearnerGapProfile {
  resultId: string;
  studentId: string | null;
  name: string;
  lrn: string;
  score: number;
  maxScore: number;
  overallPct: number;
  /** every competency, in TOS order. */
  scores: LearnerCompetencyScore[];
  /** at/above the 75 passing mark, strongest first. */
  mastered: LearnerCompetencyScore[];
  /** below 75, weakest first — this learner's remediation list. */
  leastMastered: LearnerCompetencyScore[];
}

/** A learner named in a competency's remediation list. */
export interface LearnerRef {
  resultId: string;
  studentId: string | null;
  name: string;
  lrn: string;
  earned: number;
  possible: number;
  pct: number;
  intervention: Intervention;
}

/** Per-item detail inside a competency — the "item" half of the report. */
export interface CompetencyItemStat {
  q: number;
  correct: number;
  total: number;
  /** percentage correct on this item (item MPS). */
  pct: number;
}

/** One competency across the class (report #1). */
export interface CompetencyGap {
  label: string;
  description: string;
  cognitiveLevel?: string;
  /** mapped items, restricted to those the answer key actually covers. */
  items: number[];
  /** mapped items the answer key has no entry for (excluded from the maths). */
  unkeyedItems: number[];
  /** per-item percentage correct within this competency. */
  itemStats: CompetencyItemStat[];
  possiblePerLearner: number;
  /** learners with at least one keyed item in this competency. */
  n: number;
  /** class mean percentage score on this competency (pooled earned/possible). */
  classPct: number | null;
  band: string;
  /** class-level: is the cohort as a whole at/above the 75 passing mark? */
  mastered: boolean;
  /** DepEd instructional response for the class figure. */
  intervention: Intervention;
  /** learners below 75, weakest first. */
  needing: LearnerRef[];
  /** learners at 65-74: targeted remediation, scaffolding. */
  targeted: LearnerRef[];
  /** learners at 0-64: structured, sustained intervention. */
  sustained: LearnerRef[];
  /** learners at 75-79: passing, guided practice for consistency — NOT remediation. */
  guidedPractice: LearnerRef[];
  masteredCount: number;
  /** false when fewer than MIN_ITEMS_FOR_LEARNER items measure it. */
  reliable: boolean;
}

export interface CompetencyGapReport {
  /** DepEd passing mark / mastery cut-off (75, fixed). */
  cutoff: number;
  n: number;
  competencies: CompetencyGap[];
  learners: LearnerGapProfile[];
  /** quiz items no competency claims — they contribute to the score but to no
   *  competency, so the report omits them. */
  unmappedItems: number[];
  /** labels of competencies whose items the key doesn't cover at all. */
  emptyCompetencies: string[];
  minItemsForLearner: number;
}

/** How a result is labelled: roster name, else the typed name, else the read LRN. */
function labelFor(r: ResultRecord, byId: Map<string, StudentRecord>): { name: string; lrn: string } {
  const stu = r.studentId ? byId.get(r.studentId) : undefined;
  if (stu) return { name: stu.name, lrn: stu.lrn };
  if (r.capturedName) return { name: r.capturedName, lrn: /^\d{12}$/.test(r.idDigitsRead) ? r.idDigitsRead : '' };
  const lrn = /^\d{12}$/.test(r.idDigitsRead) ? r.idDigitsRead : '';
  return { name: lrn ? `LRN ${lrn}` : 'Unassigned paper', lrn };
}

const pctOf = (earned: number, possible: number): number | null =>
  possible > 0 ? Math.round((100 * earned) / possible) : null;

/**
 * Build the competency analysis.
 *
 * Only scored results are included (an unscored or unmatched paper has nothing to
 * attribute). Percentages are rounded to whole numbers BEFORE the cut-off and band
 * comparisons, so what the report shows is what the decision used.
 */
export function analyzeCompetencies(
  results: ResultRecord[],
  key: AnswerKeyRecord,
  map: CompetencyMapRecord,
  students: StudentRecord[],
): CompetencyGapReport {
  const byId = new Map(students.map((s) => [s.id, s]));
  const scored = results.filter((r) => r.score != null && r.maxScore != null);

  // Per-result, per-item earned/possible from the shared scorer.
  const perResult = scored.map((r) => {
    const s = scoreResponse(r.rawAnswers, key);
    return { r, byQ: new Map(s.items.map((it) => [it.q, it])), score: s.score, maxScore: s.maxScore };
  });
  // Items the key defines — a mapped item the key skips can't be scored. Taken
  // from the key itself, so the coverage warnings hold even before any paper is in.
  const keyed = new Set(keyItems(key));

  const learners: LearnerGapProfile[] = perResult.map(({ r, byQ, score, maxScore }) => {
    const { name, lrn } = labelFor(r, byId);
    const scores: LearnerCompetencyScore[] = map.competencies.map((c, ci) => {
      const items = c.items.filter((q) => byQ.has(q));
      let earned = 0;
      let possible = 0;
      for (const q of items) {
        const it = byQ.get(q)!;
        earned += it.points;
        possible += it.maxPoints;
      }
      const pct = pctOf(earned, possible);
      return {
        label: competencyLabel(ci),
        description: c.description,
        earned,
        possible,
        pct,
        mastered: pct != null && pct >= MASTERY_CUTOFF,
        band: pct == null ? '—' : masteryDescriptor(pct),
        intervention: interventionFor(pct ?? 0),
        reliable: items.length >= MIN_ITEMS_FOR_LEARNER,
      };
    });
    const rated = scores.filter((s) => s.pct != null);
    return {
      resultId: r.id,
      studentId: r.studentId,
      name,
      lrn,
      score,
      maxScore,
      overallPct: pctOf(score, maxScore) ?? 0,
      scores,
      mastered: rated.filter((s) => s.mastered).sort((a, b) => b.pct! - a.pct! || a.label.localeCompare(b.label)),
      leastMastered: rated.filter((s) => !s.mastered).sort((a, b) => a.pct! - b.pct! || a.label.localeCompare(b.label)),
    };
  });

  const competencies: CompetencyGap[] = map.competencies.map((c, ci) => {
    const items = c.items.filter((q) => keyed.has(q));
    const unkeyedItems = c.items.filter((q) => !keyed.has(q));
    let earnedSum = 0;
    let possibleSum = 0;
    let n = 0;
    let masteredCount = 0;
    const needing: LearnerRef[] = [];
    const guidedPractice: LearnerRef[] = [];
    for (const l of learners) {
      const s = l.scores[ci];
      if (!s || s.pct == null) continue;
      n++;
      earnedSum += s.earned;
      possibleSum += s.possible;
      if (s.mastered) masteredCount++;
      const ref: LearnerRef = {
        resultId: l.resultId,
        studentId: l.studentId,
        name: l.name,
        lrn: l.lrn,
        earned: s.earned,
        possible: s.possible,
        pct: s.pct,
        intervention: s.intervention,
      };
      // 75-79 passed: guided practice, deliberately kept out of the remediation list.
      if (s.intervention.needsRemediation) needing.push(ref);
      else if (s.intervention.tier === 'guided') guidedPractice.push(ref);
    }
    const byWeakest = (a: LearnerRef, b: LearnerRef) => a.pct - b.pct || a.name.localeCompare(b.name);
    needing.sort(byWeakest);
    guidedPractice.sort(byWeakest);

    // Per-item percentage correct within the competency (the "item" half).
    const itemStats: CompetencyItemStat[] = items.map((q) => {
      let correct = 0;
      let total = 0;
      for (const { byQ } of perResult) {
        const it = byQ.get(q);
        if (!it) continue;
        total++;
        if (it.outcome === 'correct') correct++;
      }
      return { q, correct, total, pct: total ? Math.round((100 * correct) / total) : 0 };
    });

    const classPct = pctOf(earnedSum, possibleSum);
    const possiblePerLearner = learners.find((l) => l.scores[ci]?.pct != null)?.scores[ci].possible ?? 0;
    return {
      label: competencyLabel(ci),
      description: c.description,
      cognitiveLevel: c.cognitiveLevel,
      items,
      unkeyedItems,
      itemStats,
      possiblePerLearner,
      n,
      classPct,
      band: classPct == null ? '—' : masteryDescriptor(classPct),
      mastered: classPct != null && classPct >= MASTERY_CUTOFF,
      intervention: interventionFor(classPct ?? 0),
      needing,
      targeted: needing.filter((l) => l.intervention.tier === 'targeted'),
      sustained: needing.filter((l) => l.intervention.tier === 'sustained'),
      guidedPractice,
      masteredCount,
      reliable: items.length >= MIN_ITEMS_FOR_LEARNER,
    };
  });

  const claimed = new Set(map.competencies.flatMap((c) => c.items));
  const unmappedItems = [...keyed].filter((q) => !claimed.has(q)).sort((a, b) => a - b);

  return {
    cutoff: MASTERY_CUTOFF,
    n: learners.length,
    competencies,
    learners: learners.sort((a, b) => a.name.localeCompare(b.name)),
    unmappedItems,
    emptyCompetencies: competencies.filter((c) => !c.items.length).map((c) => c.label),
    minItemsForLearner: MIN_ITEMS_FOR_LEARNER,
  };
}

/** Competencies the class has not yet mastered, weakest first — the class's
 *  least-mastered list, and the natural order for a remediation plan. */
export function leastMasteredFirst(report: CompetencyGapReport): CompetencyGap[] {
  return report.competencies
    .filter((c) => c.classPct != null)
    .sort((a, b) => a.classPct! - b.classPct! || a.label.localeCompare(b.label));
}
